// The chat transcript, rendered.
//
// Extracted verbatim from `ChatPanel.js` so the DASHBOARD thread and the
// in-project chat rail share one renderer. Two things made this worth extracting
// rather than copying:
//
//   * It owns the cross-repo message contract — `content` is RAW TEXT (there is
//     no markdown renderer and no `dangerouslySetInnerHTML` anywhere in `src/`),
//     and `actions` rides BESIDE `content` because a tile id is an opaque uuid a
//     model must never be trusted to compose into a URL. That rule is pinned in
//     `panels/CONTEXT.md` and in the backend's "What we expose" section; two
//     copies means two places it can be broken silently.
//   * It is the only way any of this gets a render test. `ChatPanel` cannot be
//     mounted under jest (it pulls `api/chat`, `chatRounds`, `interactionLog`,
//     `useConfirm`, `useToast`); this file imports nothing but its props.
//
// `onOpenTile` is deliberately a HOST POLICY rather than behaviour baked in here:
// the project editor navigates in place (`openTileById` → the existing
// `openTab`/`goToTile`), while the dashboard opens a new browser tab — because
// there, tearing down the conversation to go pick faces is the thing we are
// trying to avoid.
//
// It owns its own stylesheet, and that is load-bearing rather than tidy: every
// class below used to be styled from `ChatPanel.css`, which only `ChatPanel.js`
// imports and which therefore ships in ProjectPage's LAZY chunk — so on a fresh
// load of the dashboard the transcript had no styling at all. See
// `ChatMessageList.css`'s header for the full account.

import './ChatMessageList.css';

// Tool calls are one summary STRING (`chatRounds.summarizeToolCall`'s
// newline-joined form, mirrored server-side by `agent_runner/loop.py`), so the
// count is its line count. Nothing structured survives the save — see
// `chatMessages.toSaveable`.
function actionCount(toolSummary) {
  return toolSummary.split('\n').filter((line) => line.trim()).length;
}

/**
 * @param messages   the shapes `panels/chatMessages.js` produces. A message's
 *   optional `display` overrides `content` for rendering; `''` hides the row (see
 *   `chatMessages.normalizeDisplay`).
 * @param onOpenTile (tileId) => void. Omit and no action controls render.
 * @param isTileKnown optional (tileId) => boolean. See below — the DEFAULT is
 *   "render it", and getting that backwards hides the control entirely.
 */
export default function ChatMessageList({ messages, onOpenTile, isTileKnown }) {
  // `isTileKnown` absent means "the host cannot verify tile ids", NOT "no tile is
  // known. The distinction is load-bearing and was a real bug in the making: the
  // original inline code was `nodes?.some((n) => n.id === a.tileId)`, which
  // evaluates to `undefined` when there is no graph — so a host without one
  // (the dashboard never loads the graph; the server owns it during a run) would
  // have had EVERY action button silently filtered away, which looks exactly like
  // the backend having failed to attach one.
  //
  // The project editor passes a real predicate, preserving its stated reason: a
  // tile deleted since the question was asked must render nothing rather than a
  // control that dead-ends. The dashboard passes none, which is correct there —
  // the tileId arrived from the same `/runs` poll that produced `parkedTiles`, so
  // it is already server-resolved.
  const visibleActions = (msg) => {
    const actions = msg.actions;
    if (!actions?.length || !onOpenTile) return [];
    if (typeof isTileKnown !== 'function') return actions;
    return actions.filter((a) => isTileKnown(a.tileId));
  };

  return messages.map((msg, i) => {
    const actions = visibleActions(msg);
    // `display` overrides `content` for RENDERING only — see
    // `chatMessages.normalizeDisplay`. `content` is what the model was sent, and
    // that is frequently not what the user said: the composer wraps a typed prompt
    // in kickoff scaffolding, and the orchestration loop synthesizes entire user
    // turns. Persisting them is required (the stored history is replayed into the
    // next prompt), so hiding them has to happen here.
    //
    // `=== ''` is a HIDE, not a falsy check: absent means "no override, render
    // content", and conflating the two would put every "(You are now the subagent
    // …)" turn back on screen. A hidden message still renders nothing at all —
    // not an empty bubble — because an empty bubble reads as a failed reply.
    const text = typeof msg.display === 'string' ? msg.display : msg.content;
    if (text === '' && !msg.streaming && !msg.toolSummary && !actions.length) return null;
    return (
      <div
        key={i}
        className={`chat-message chat-message-${msg.role} ${msg.error ? 'chat-message-error' : ''}`}
      >
        <div className="chat-bubble">
          {text}
          {msg.streaming && <span className="chat-cursor">▊</span>}
        </div>
        {/* COLLAPSED by default. A turn runs up to `MAX_ROUNDS` (16) and appends a
            batch of calls per round, so expanded this buried the conversation the
            user is actually reading under argument dumps. `<details>` rather than
            our own toggle so the disclosure is keyboard- and screen-reader-
            accessible with no ARIA of ours, and so it needs no state — the browser
            keeps it, per message, across re-renders from the 2 s poll. */}
        {msg.toolSummary && (
          <details className="chat-tool-summary">
            <summary>
              ⚙ {actionCount(msg.toolSummary)}{' '}
              {actionCount(msg.toolSummary) === 1 ? 'action' : 'actions'}
            </summary>
            <pre>{msg.toolSummary}</pre>
          </details>
        )}
        {actions.length > 0 && (
          <div className="chat-message-actions">
            {actions.map((a) => (
              <button
                key={a.tileId}
                type="button"
                className="chat-action-link"
                onClick={() => onOpenTile(a.tileId)}
              >
                {a.label || 'Open module'} →
              </button>
            ))}
          </div>
        )}
      </div>
    );
  });
}
